// components/Services.jsx — PREMIUM DESIGN ✅
import { useNavigate } from 'react-router-dom';

export default function Services() {
  const navigate = useNavigate();

  const services = [
    {
      title: "Google Ads",
      desc: "Precision-engineered Search, Display & YouTube campaigns delivering measurable results with advanced targeting.",
      lottie: "https://lottie.host/a1ab1dcf-81d8-4f83-8929-4522d3a6eb83/rc2ckcBMvU.lottie",
      stat: "3.8x ROAS",
      features: ["Search & Shopping", "YouTube Pre-rolls", "Display Network", "Performance Max"],
      color: "#4285F4",
    },
    {
      title: "Meta Ads",
      desc: "Facebook & Instagram performance campaigns optimized for scale, conversions, and audience growth.",
      lottie: "https://lottie.host/295ef53e-ea1b-4b8d-a910-d5cb4d0e3523/B9jcQoK6gh.lottie",
      stat: "12M+ Reach",
      features: ["Lead Generation", "Dynamic Retargeting", "Lookalike Audiences", "Catalog Sales"],
      color: "#0668E1",
    },
    {
      title: "Creative Studio",
      desc: "High-converting ad creatives with systematic A/B testing & dynamic creative optimization.",
      lottie: "https://lottie.host/c1a833ab-d869-48fe-9129-1c9997f05296/TmIwvPte89.lottie",
      stat: "47% CR Lift",
      features: ["Video Production", "Static Banners", "Motion Graphics", "UGC Content"],
      color: "#E4405F",
    },
    {
      title: "Analytics Hub",
      desc: "Real-time ROI dashboards & predictive insights across all channels for smarter decision-making.",
      lottie: "https://lottie.host/73d68fcd-70cc-4cd1-8a1e-30cd012a7170/7P0rUH0shv.lottie",
      stat: "99.9% Uptime",
      features: ["Live Dashboards", "Attribution Models", "Predictive AI", "Custom Reports"],
      color: "#00809D",
    },
  ];

  return (
    <section className="relative py-20 sm:py-24 lg:py-28 bg-white overflow-hidden">
      {/* Backgrounds */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
        <div className="absolute inset-0 opacity-[0.12]" style={{
          backgroundImage: 'radial-gradient(circle, #00809D 0.5px, transparent 0.5px)',
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-1/4 -left-20 w-[400px] h-[400px] bg-[#00809D]/[0.03] rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 -right-20 w-[400px] h-[400px] bg-[#00809D]/[0.03] rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-14 sm:mb-16 lg:mb-20">
          <div className="si" style={{ '--d': '0s' }}>
            <span className="inline-flex items-center gap-2 text-[#00809D] text-xs font-bold uppercase tracking-[0.15em] bg-[#00809D]/[0.06] px-4 py-2 rounded-full border border-[#00809D]/10 mb-5 sm:mb-6">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
              What We Do
            </span>
          </div>

          <div className="si" style={{ '--d': '0.1s' }}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-black text-gray-900 leading-[1.1] tracking-tight mb-4 sm:mb-5">
              Services Built For
              <span className="text-[#00809D]"> Growth</span>
            </h2>
          </div>

          <div className="si" style={{ '--d': '0.2s' }}>
            <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              Precision platforms engineered for predictable, scalable growth across every channel that matters.
            </p>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 mb-14 sm:mb-16">
          {services.map((s, i) => (
            <div
              key={i}
              className="group relative bg-white rounded-2xl sm:rounded-3xl border border-gray-200 hover:border-[#00809D]/20 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#00809D]/10 overflow-hidden si"
              style={{ '--d': `${0.2 + i * 0.1}s` }}
            >
              {/* Top color accent */}
              <div className="h-1 w-full transition-all duration-500 group-hover:h-1.5" style={{ backgroundColor: s.color }} />

              <div className="p-5 sm:p-6 flex flex-col h-full">
                {/* Icon + Stat row */}
                <div className="flex items-start justify-between mb-4 sm:mb-5">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gray-50 border border-gray-100 p-2 sm:p-2.5 group-hover:scale-110 group-hover:shadow-lg transition-all duration-500 flex items-center justify-center overflow-hidden">
                    <dotlottie-wc
                      src={s.lottie}
                      autoplay
                      loop
                      style={{ width: '100%', height: '100%', display: 'block' }}
                    />
                  </div>
                  <div className="flex items-center gap-1 bg-emerald-50 text-emerald-600 text-[10px] sm:text-xs font-bold px-2 sm:px-2.5 py-1 rounded-lg border border-emerald-100">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                    {s.stat}
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-[#00809D] transition-colors mb-2">
                  {s.title}
                </h3>

                {/* Description */}
                <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">
                  {s.desc}
                </p>

                {/* Features */}
                <div className="space-y-2 mb-5">
                  {s.features.map((f, fi) => (
                    <div key={fi} className="flex items-center gap-2 text-sm">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.color + '15' }}>
                        <svg className="w-2.5 h-2.5" fill="none" stroke={s.color} strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <span className="text-gray-600 text-xs sm:text-sm">{f}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <button
                  onClick={() => navigate('/contact')}
                  className="w-full flex items-center justify-center gap-2 bg-gray-50 hover:bg-[#00809D] text-gray-600 hover:text-white text-sm font-semibold py-2.5 sm:py-3 rounded-xl border border-gray-200 hover:border-[#00809D] transition-all duration-300 group/btn"
                >
                  Learn More
                  <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center si" style={{ '--d': '0.7s' }}>
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 bg-gray-50 border border-gray-200 rounded-2xl px-6 sm:px-8 py-5 sm:py-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#00809D]/10 flex items-center justify-center">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-[#00809D]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              </div>
              <div className="text-left">
                <p className="text-gray-900 font-bold text-sm sm:text-base">Not sure which service fits?</p>
                <p className="text-gray-400 text-xs sm:text-sm">Book a free 15-min strategy call</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/contact')}
              className="bg-[#00809D] hover:bg-[#006d87] text-white px-6 py-2.5 sm:py-3 rounded-xl font-bold text-sm shadow-lg shadow-[#00809D]/20 hover:shadow-xl hover:shadow-[#00809D]/30 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300 whitespace-nowrap"
            >
              Talk to an Expert
            </button>
          </div>
        </div>
      </div>

      {/* Bottom border */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      <style>{`
        .si { opacity:0; transform:translateY(20px); animation: sIn .7s cubic-bezier(.16,1,.3,1) forwards; animation-delay: var(--d); }
        @keyframes sIn { to { opacity:1; transform:translateY(0); } }
      `}</style>
    </section>
  );
}
